// ═══════════════════════════════════════════════════════════════════════════
// PAGE: DASHBOARD
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Note : `const pages = {}` (init du registry global) a été supprimé ici
// et déplacé dans core.ts (init runtime conditionnel sur globalThis.pages).

import { consumeLastModifiedAnimation } from '../utils/last-modified-anim.js';
import { openProjectPilotPicker } from '../utils/pilot-picker.js';
import { buildProjectGeneralTab } from './page-projects-modal-fragments.js';
import {
  buildCommitteePilotRows,
  buildModalTabs,
  buildPilotChips,
  buildPilotsCell,
  buildPilotsTab,
  createPilotChipsRefresher,
  indexCommitteePilots,
  loadPilotRefs,
  pilotsSortKey,
  readCommitteePilots,
  wireCodeUppercase,
  wireCommitteePilotDots,
  wireModalTabs,
  type CommitteePilot,
  type UserMin,
} from './pilots-form.js';

interface ProjectFull {
  id: number;
  code?: string | null;
  name: string;
  current_stage?: string | null;
  risk_list_id?: number | null;
  // ID de la révision en cours, propagé par le backend (cf. ProjectOut).
  // None si aucune révision ouverte → le lien "Risque / révision en cours" est
  // masqué côté UI.
  current_revision_id?: number | null;
  pilots?: number[];
  committee_pilots?: CommitteePilot[];
}

export interface StageRow {
  id: number;
  name: string;
}

export interface RiskListRow {
  id: number;
  name: string;
}

declare global {
  interface Window {
    renderDashboard: typeof renderDashboard;
    showProjectModal: typeof showProjectModal;
  }
}

pages.dashboard = async function () {
  setHeader(
    t('vocabulary.project_plural'),
    `<button class="btn btn-primary ml-auto" id="btn-new-project">${esc(t('pageProjects.btnAdd'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [projects, stages, riskLists, users] = (await Promise.all([
      api.getProjects(),
      loadStages(),
      // Les listes de risques ne servent ici qu'à préremplir la modale de
      // création de projet : leur absence ne doit pas emporter la page. Sans ce
      // repli, un utilisateur sans `risk_lists.read` (palier Essentiel, par
      // exemple) reçoit un 403 qui rejette tout le `Promise.all` — le `catch`
      // ci-dessous remplace alors la liste des projets par un message d'erreur,
      // alors que `getProjects()` a réussi. Même repli que `getRevisions()` sur
      // la fiche projet.
      loadRiskLists().catch(() => []),
      api.getUsers(),
    ])) as [ProjectFull[], StageRow[], RiskListRow[], UserMin[]];
    renderDashboard(projects, users);
    document
      .getElementById('btn-new-project')
      ?.addEventListener('click', () => void showProjectModal(null, stages, riskLists));
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

// `stages` / `riskLists` ne sont plus paramètres : ils ne servaient qu'au
// câblage de `#btn-new-project` retiré ci-dessous (doublon du header).
function renderDashboard(projects: ProjectFull[], users: ReadonlyArray<UserMin>): void {
  const userMap: Record<number, UserMin> = Object.fromEntries((users || []).map((u) => [u.id, u]));
  if (!projects.length) {
    setContent(`<div class="empty">${t('pageProjects.empty')}</div>`);
    return;
  }

  let pSortKey: 'code' | 'name' | 'stage' | 'pilots' = 'code';
  let pSortDir: 'asc' | 'desc' = 'asc';

  const sortProjects = (arr: ReadonlyArray<ProjectFull>): ProjectFull[] =>
    [...arr].sort((a, b) => {
      let va: string;
      let vb: string;
      switch (pSortKey) {
        case 'code':
          va = (a.code || '').toLowerCase();
          vb = (b.code || '').toLowerCase();
          break;
        case 'name':
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
          break;
        case 'stage':
          va = (a.current_stage || '').toLowerCase();
          vb = (b.current_stage || '').toLowerCase();
          break;
        case 'pilots':
          va = pilotsSortKey(a.pilots, userMap);
          vb = pilotsSortKey(b.pilots, userMap);
          break;
        default:
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
      }
      return (va < vb ? -1 : va > vb ? 1 : 0) * (pSortDir === 'asc' ? 1 : -1);
    });

  const pTh = (key: 'code' | 'name' | 'stage' | 'pilots', label: string): string => {
    const active = pSortKey === key;
    return `<th data-sort-proj="${key}" class="cursor-pointer" style="user-select:none">${label} <span class="text-xs">${active ? (pSortDir === 'asc' ? '↑' : '↓') : '↕'}</span></th>`;
  };

  const pilotsCell = (p: ProjectFull): string =>
    buildPilotsCell(p.pilots, userMap, t('pageProjects.warnNoPilot'));

  const buildRows = (): string =>
    sortProjects(projects)
      .map(
        (p) => `
      <tr data-project-id="${p.id}">
        <td class="cursor-pointer text-mono text-sm font-bold proj-code-cell" style="text-decoration:underline" data-open-project="${p.id}">${esc(p.code || '—')}</td>
        <td class="cursor-pointer proj-name-cell" data-open-project="${p.id}" style="color:var(--text);font-weight:500">${esc(p.name)}</td>
        <td class="proj-stage-cell">${esc(p.current_stage || '—')}</td>
        <td class="proj-pilots-cell">${pilotsCell(p)}</td>
        <td class="text-nowrap">
          <a href="#stats/project-synthesis?proj=${p.id}&locked=1"
             title="${esc(t('pageProjects.btnSynthTitle', { object: t('vocabulary.project') }))}"
             style="font-size:12px;color:var(--accent);text-decoration:underline;margin-right:10px">${esc(t('pageProjects.btnSynth'))}</a>
          ${
            p.current_revision_id && window.hasPermission && window.hasPermission('risk_lists.read')
              ? `<a href="#revision/${p.current_revision_id}"
                  style="font-size:12px;color:var(--accent);text-decoration:underline;margin-right:10px">${esc(t('pageProjects.btnCurrentRev'))}</a>`
              : ''
          }
          <button class="btn btn-secondary btn-icon ml-1" data-edit-project="${p.id}" title="${esc(t('pageProjects.btnEdit'))}">✎</button>
          <button class="btn btn-danger btn-icon ml-1" data-delete-project="${p.id}" data-project-name="${esc(p.name)}" title="${esc(t('pageProjects.btnDelete'))}">✕</button>
        </td>
      </tr>`,
      )
      .join('');

  setContent(`
    <div class="card">
      <div class="table-wrap">
        <table id="projects-table">
          <thead><tr>
            ${pTh('code', esc(t('pageProjects.colCode')))}${pTh('name', esc(t('pageProjects.colName')))}${pTh('stage', esc(t('pageProjects.colStage')))}${pTh('pilots', esc(t('pageProjects.colPilots')))}<th></th>
          </tr></thead>
          <tbody id="projects-tbody">${buildRows()}</tbody>
        </table>
      </div>
    </div>`);

  document.querySelector('#projects-table thead')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    const h = target?.closest<HTMLElement>('[data-sort-proj]');
    if (!h) return;
    const key = h.dataset['sortProj'] as 'code' | 'name' | 'stage' | 'pilots';
    if (pSortKey === key) pSortDir = pSortDir === 'asc' ? 'desc' : 'asc';
    else {
      pSortKey = key;
      pSortDir = 'asc';
    }
    const tbody = document.getElementById('projects-tbody');
    if (tbody) tbody.innerHTML = buildRows();
    document.querySelectorAll<HTMLElement>('[data-sort-proj]').forEach((th) => {
      const a = th.dataset['sortProj'] === pSortKey;
      const span = th.querySelector('span');
      if (span) span.textContent = a ? (pSortDir === 'asc' ? '↑' : '↓') : '↕';
    });
  });

  document.getElementById('projects-tbody')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.tagName === 'A') return;
    // Le bouton "Synthèse KPI" est devenu un lien <a> (RefactoUX mai 2026) qui
    // navigue vers la page de synthèse ; aucune interception JS nécessaire.
    // RefactoUX mai 2026 : clic Code/Nom/✎ → navigation vers la page
    // d'édition `#project/:id` (anciennement openModal). Création ad-hoc
    // garde la popup (cf. btn-new-project ci-dessous).
    // Les cellules Code/Nom portent `data-open-project` (et non
    // `data-edit-project`) : ainsi le mode mobile-readonly, qui masque les
    // affordances `[data-edit-*]`, ne fait pas disparaître ces cellules de
    // données (sinon la colonne Code s'effondrait et l'en-tête « Code »
    // surplombait l'étape — bug mobile mai 2026). Seul le bouton ✎ garde
    // `data-edit-project` et reste donc masqué en mobile.
    const openCell = target.closest<HTMLElement>('[data-open-project]');
    const editBtn = target.closest<HTMLElement>('[data-edit-project]');
    const navEl = openCell || editBtn;
    if (navEl && !target.closest('[data-delete-project]')) {
      const id = parseInt(navEl.dataset['openProject'] || navEl.dataset['editProject'] || '0');
      if (id > 0) window.location.hash = '#project/' + id;
      return;
    }
    const delBtn = target.closest<HTMLElement>('[data-delete-project]');
    if (delBtn) {
      const id = parseInt(delBtn.dataset['deleteProject'] || '0');
      const name = delBtn.dataset['projectName'] || '';
      openConfirmDangerModal(
        t('pageProjects.delTitle', { object: t('vocabulary.project') }),
        t('pageProjects.delConfirm', { name }),
        async () => {
          await api.deleteProject(id);
          toast(t('pageProjects.toastDel'));
          void pages.dashboard?.({});
        },
      );
    }
  });
  // NE PAS recâbler `#btn-new-project` ici : `pages.dashboard` le fait déjà
  // juste après `setHeader`, et ce bouton vit dans le header que `setContent()`
  // ne remplace pas. Le lier aux deux endroits posait DEUX listeners sur le
  // même élément dès la première entrée de page : un clic lançait deux
  // `showProjectModal()` concurrentes, et comme la fonction est `async` (elle
  // attend users + comités), la seconde rappelait `openModal` APRÈS le début de
  // la saisie — détruisant la modale en cours et effaçant les champs déjà
  // remplis, sans aucun message. Même défaut corrigé sur la page Structures.
  // Le délai dépendant du volume de données, ça ne se voyait pas sur un tenant
  // vide (bug trouvé par l'e2e sur base seedée, 2026-07-25).
  // Animation post-MAJ : si une page d'édition a posé un drapeau
  // 'lastModified:projects', on flashe la ligne correspondante.
  setTimeout(
    () =>
      consumeLastModifiedAnimation('projects', (id) =>
        document.querySelector<HTMLTableRowElement>(`#projects-table tr[data-project-id="${id}"]`),
      ),
    50,
  );
}

async function showProjectModal(
  project: ProjectFull | null,
  stages: ReadonlyArray<StageRow>,
  riskLists: ReadonlyArray<RiskListRow>,
  onSaved?: (saved: ProjectFull) => void,
): Promise<void> {
  const isNew = !project;
  const code = project ? project.code || '' : '';
  const name = project ? project.name : '';
  const currentStage = project ? project.current_stage || '' : '';
  const riskListId = project ? project.risk_list_id || '' : '';
  const { indivUsers, committees } = await loadPilotRefs();
  const initialPilots = new Set<number>((project && project.pilots) || []);

  // Onglet Général : rendu pur délégué à page-projects-modal-fragments.
  const tabGeneralHtml = buildProjectGeneralTab(
    code,
    name,
    currentStage,
    stages,
    riskListId,
    riskLists,
    isNew,
  );

  const selectedPilots = new Set<number>(initialPilots);
  // Puces, onglets Pilotes / Comités et barre d'onglets : fragments partagés
  // avec la modale programme (pilots-form). Le Set mutable reste ici.
  const renderChips = (): string =>
    buildPilotChips(selectedPilots, indivUsers, 'pm') ||
    `<span style="color:var(--text3);font-size:11px">${esc(t('pageProjects.pilots.empty'))}</span>`;

  const body = buildModalTabs('pm', {
    general: { label: t('pageProjects.tab.general'), html: tabGeneralHtml },
    pilots: {
      label: t('pageProjects.tab.pilots', { object: t('vocabulary.project') }),
      html: buildPilotsTab('pm', renderChips(), {
        helpHtml: t('pageProjects.pilots.help'),
        addTitle: t('pageProjects.pilots.btnAddTitle'),
      }),
    },
    committees: {
      label: t('pageProjects.tab.committees', { plural: t('vocabulary.committee_plural') }),
      html: buildCommitteePilotRows(
        committees,
        indexCommitteePilots(project?.committee_pilots),
        indivUsers,
        {
          prefix: 'pm',
          missingLabel: t('pageProjects.pilots.notSet'),
          emptyLabel: t('pageProjects.committees.empty'),
        },
      ),
    },
  });

  openModal(
    isNew
      ? t('pageProjects.titleNew', { object: t('vocabulary.project') })
      : t('pageProjects.titleEdit', { object: t('vocabulary.project') }),
    body,
    async () => {
      const codeEl = document.getElementById('pm-code') as HTMLInputElement | null;
      const nameEl = document.getElementById('pm-name') as HTMLInputElement | null;
      const codeVal = (codeEl?.value || '').trim().toUpperCase();
      const nameVal = nameEl?.value.trim() || '';
      if (!codeVal) {
        toast(t('pageProjects.errCode'), 'error');
        return;
      }
      if (!nameVal) {
        toast(t('pageProjects.errName'), 'error');
        return;
      }
      const stageEl = document.getElementById('pm-stage') as HTMLSelectElement | null;
      const listEl = document.getElementById('pm-risklist') as HTMLSelectElement | null;
      const stageVal = stageEl?.value || null;
      const listVal = parseInt(listEl?.value || '0') || null;
      const pilots = Array.from(selectedPilots);
      const committee_pilots = readCommitteePilots('pm');
      const payload = {
        code: codeVal,
        name: nameVal,
        current_stage: stageVal,
        risk_list_id: listVal,
        pilots,
        committee_pilots,
      };
      try {
        if (isNew) {
          const created = (await api.createProject(payload)) as ProjectFull;
          toast(t('pageProjects.toastNew'));
          closeModal();
          if (onSaved) onSaved(created);
          else void pages.dashboard?.({});
        } else if (project) {
          const updated = (await api.updateProject(project.id, payload)) as ProjectFull;
          toast(t('pageProjects.toastEdit'));
          closeModal();
          if (onSaved) onSaved(updated);
          else void pages.dashboard?.({});
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('pageProjects.btnNew') : t('pageProjects.btnSave'),
  );

  setTimeout(() => {
    // Mode US (mai 2026) : verrouille la combo Liste de risques standard à
    // la création / édition de projet (les listes de risques sont une feature
    // de l'offre Équipe).
    if (window.isSoloUser?.() && window.markLocked) {
      const listEl = document.getElementById('pm-risklist');
      if (listEl) window.markLocked(listEl.parentElement || listEl, 'solo');
    }

    wireCodeUppercase();
    wireModalTabs('pm');

    const refreshChips = createPilotChipsRefresher('pm', renderChips, selectedPilots);
    refreshChips();

    document.getElementById('pm-pilot-add-btn')?.addEventListener('click', (ev: Event) => {
      ev.stopPropagation();
      openProjectPilotPicker({
        anchor: ev.currentTarget as HTMLElement,
        users: indivUsers,
        selected: selectedPilots,
        onClose: refreshChips,
      });
    });

    wireCommitteePilotDots('pm', t('pageProjects.pilots.notSet'));
  }, 0);
}

// ── ESM globals shim (lot B.8 + S5.3) ──
window.renderDashboard = renderDashboard;
window.showProjectModal = showProjectModal;

export {};
